/**
 * Heading - Semantic heading component that replaces complex Typography usage
 * Makes it easy for content editors to create proper heading hierarchy
 */

import React from 'react'
import { Typography } from '@mui/material'
import { useTheme } from '@mui/material/styles'

interface HeadingProps {
	level: 1 | 2 | 3 | 4 | 5 | 6
	children: React.ReactNode
	color?: 'primary' | 'secondary' | 'white' | 'inherit'
	align?: 'left' | 'center' | 'right'
	weight?: 'normal' | 'medium' | 'bold'
}

const Heading: React.FC<HeadingProps> = ({ level, children, color = 'inherit', align = 'left', weight = 'normal' }) => {
	const theme = useTheme()

	const getVariant = () => {
		switch (level) {
			case 1:
				return 'h1'
			case 2:
				return 'h2'
			case 3:
				return 'h3'
			case 4:
				return 'h4'
			case 5:
				return 'h5'
			case 6:
				return 'h6'
			default:
				return 'h1'
		}
	}

	const getColor = () => {
		switch (color) {
			case 'primary':
				return theme.palette.primary.main
			case 'secondary':
				return theme.palette.secondary.main
			case 'white':
				return 'white'
			case 'inherit':
				return 'inherit'
			default:
				return 'inherit'
		}
	}

	const getFontWeight = () => {
		switch (weight) {
			case 'normal':
				return 400
			case 'medium':
				return 600
			case 'bold':
				return 700
			default:
				return 400
		}
	}

	return (
		<Typography
			variant={getVariant()}
			component={`h${level}`}
			sx={{
				color: getColor(),
				textAlign: align,
				fontWeight: getFontWeight(),
				lineHeight: 1.3
			}}
		>
			{children}
		</Typography>
	)
}

export default Heading
